/**
 * Path of Figma's "Thumbnail" asset (grid body panel, node 3153:43607): a
 * 180×90 sheet whose top edge scoops down ~9px between x≈52–125. Stretched
 * non-uniformly to fill the panel box. Shared by the web (glass) and native
 * (flat) DeckCardBodyPanel variants.
 *
 * BOTTOM CORNERS are radius 28, not Figma's 21: the panel's bottom edge IS
 * the card's bottom edge, and the card clips it (`overflow-hidden` +
 * `rounded-[28px]`). At r=21 the panel fell short of that clip, leaving a
 * crescent of bare card background in each bottom corner — invisible on a
 * pale deck, obvious on a saturated one. Drawing them at the card's own
 * radius lets the clip define the corner exactly.
 */
export const BODY_PANEL_PATH =
  'M0 20.9898V30.0035V62C0 77.4641 12.5359 90 28 90H152C167.464 90 ' +
  '180 77.4641 180 62V20.9898C180 9.39747 170.603 0 159.01 0H125.524C121.321 ' +
  '0 117.29 1.67147 114.32 4.64603C111.296 7.67448 107.175 9.34989 102.896 ' +
  '9.29054L73.1615 8.87813C69.2123 8.82335 65.4401 7.23119 62.646 4.43979C59.8003 ' +
  '1.59689 55.9424 0 51.92 0H20.9898C9.39746 0 0 9.39746 0 20.9898Z';

/**
 * The EXPANDED deck panel (Figma node 5656:17008 `Vector 2281`, viewBox
 * 225×110 — the revised "Cards Updated" deck card, drawn at true size), with
 * the bottom corners redrawn at r28 instead of Figma's 21: the panel's bottom
 * edge is clipped by the card's `rounded-[28px]`, and at r21 it falls short
 * of that clip — the bare-corner crescent the compact path's comment
 * describes.
 */
export const BODY_PANEL_EXPANDED_PATH =
  'M0 20.9898V36.6709V82C0 97.4641 12.5359 110 28 110H197C212.464 110 ' +
  '225 97.4641 225 82V20.9898C225 9.39747 215.603 0 204.01 0H156.642C151.533 ' +
  '0 146.611 1.91761 142.849 5.37332C139.087 8.82904 134.165 10.7466 129.057 ' +
  '10.7466H90.4247C85.6831 10.7466 81.1484 8.80486 77.8762 5.37332C74.604 ' +
  '1.94179 70.0693 0 65.3277 0H20.9898C9.39747 0 0 9.39747 0 20.9898Z';

/**
 * The OPEN top edge of the panel (right corner arc → scoop → left corner
 * arc, endpoints at y≈21 where the shape meets the card's own border).
 * Stroked separately from the fill: the panel's left/right/bottom must NOT
 * draw their own line — there the panel edge coincides with the DeckCard's
 * 1px border, and a second stroke reads as a doubled, thicker border.
 */
export const BODY_PANEL_TOP_EDGE_PATH =
  'M180 20.9898C180 9.39747 170.603 0 159.01 0H125.524C121.321 0 117.29 ' +
  '1.67147 114.32 4.64603C111.296 7.67448 107.175 9.34989 102.896 ' +
  '9.29054L73.1615 8.87813C69.2123 8.82335 65.4401 7.23119 62.646 ' +
  '4.43979C59.8003 1.59689 55.9424 0 51.92 0H20.9898C9.39746 0 0 9.39746 0 20.9898';

/**
 * Open top edge of the EXPANDED deck panel — {@link BODY_PANEL_TOP_EDGE_PATH}
 * scaled 1.25×, i.e. the top section of {@link BODY_PANEL_EXPANDED_PATH}.
 * Native draws fill + edge in ONE svg whose viewBox is the panel geometry's,
 * so the edge must be authored in the SAME coordinate space as the fill —
 * feeding the 180×90 edge into the 225×112.5 viewBox renders a shrunken
 * stroke cutting across the panel.
 */
export const BODY_PANEL_EXPANDED_TOP_EDGE_PATH =
  'M225 20.9898C225 9.39747 215.603 0 204.01 0H156.642C151.533 0 146.611 ' +
  '1.91761 142.849 5.37332C139.087 8.82904 134.165 10.7466 129.057 ' +
  '10.7466H90.4247C85.6831 10.7466 81.1484 8.80486 77.8762 ' +
  '5.37332C74.604 1.94179 70.0693 0 65.3277 0H20.9898C9.39747 0 0 9.39747 0 20.9898';

/**
 * FolderCard / StudyGroupCard COMPACT body panel (Figma 5714:29353
 * `Vector 2282`, viewBox 180×98 — the revised taller compact panel). The
 * top edge is asymmetric: a scoop on the left-centre then a RAISED right
 * shoulder (right edge tops out at y≈10), matching the folder shape above
 * it. The full closed shape serves as BOTH the glass mask and the border
 * stroke, and the card does not clip the panel — so the blur can never
 * spill past the visible edge, and this rounded bottom is the card's real
 * bottom corner. (DeckCard, by contrast, strokes only its top edge over a
 * separate border overlay.)
 */
export const FOLDER_PANEL_COMPACT_PATH =
  'M0 20.9898V32.6704V77.0102C0 88.6025 9.39748 98 20.9898 98H159.01C170.603 ' +
  '98 180 88.6026 180 77.0102V30.8005C180 19.1162 170.459 9.68115 158.775 ' +
  '9.81201L122.575 10.2175H109.59L73.4377 9.67147C69.3299 9.60943 65.4283 ' +
  '7.86112 62.648 4.83659C59.8145 1.7542 55.8191 0 51.6322 0H20.9898C9.39746 ' +
  '0 0 9.39748 0 20.9898Z';

/**
 * The EXPANDED folder panel (Figma node 5656:17097 `Vector 2282`, viewBox
 * 270×110 — the revised "Folders Updated" card, 5656:17083). Same 110 height
 * as the deck panel; the raised shoulder sits on the LEFT in this revision.
 * Bottom corners stay Figma's r21 — this panel IS the card's real bottom
 * edge (no card clip), so there is no crescent to compensate for.
 */
export const FOLDER_PANEL_EXPANDED_PATH =
  'M0 20.9898V36.6709V89.0102C0 100.603 9.39747 110 20.9898 110H249.01C260.603 ' +
  '110 270 100.603 270 89.0102V31.9131C270 20.252 260.495 10.8263 248.834 ' +
  '10.924L183.862 11.4686H164.385L107.834 10.8295C103.088 10.7758 98.5009 ' +
  '9.1155 94.8203 6.11935L93.0907 4.71147C89.3469 1.66394 84.6671 0 79.8398 ' +
  '0H20.9898C9.39747 0 0 9.39747 0 20.9898Z';

/** Panel geometry per shape + size: the path and the viewBox it's drawn in. */
export const PANEL_GEOMETRY = {
  deck: { path: BODY_PANEL_PATH, viewBox: '0 0 180 90' },
  deckExpanded: { path: BODY_PANEL_EXPANDED_PATH, viewBox: '0 0 225 110' },
  folder: { path: FOLDER_PANEL_COMPACT_PATH, viewBox: '0 0 180 98' },
  folderExpanded: { path: FOLDER_PANEL_EXPANDED_PATH, viewBox: '0 0 270 110' },
} as const;

export type PanelGeometryKey = keyof typeof PANEL_GEOMETRY;

/** Which {@link PANEL_GEOMETRY} entry a card's shape + size maps to. The
 *  study-group card shares the folder geometry at BOTH sizes (5729:10436
 *  adopted the folder-expanded revision); it differs only in panel content. */
export function panelGeometryKey(
  shape: 'deck' | 'folder',
  size: 'compact' | 'expanded',
): PanelGeometryKey {
  if (size !== 'expanded') return shape;
  return shape === 'folder' ? 'folderExpanded' : 'deckExpanded';
}
